import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Zap, Film, Share2, Users, Sparkles, Layers, MessageSquare, Play, Palette } from 'lucide-react';
import { supabase } from '../services/supabaseClient';

interface LandingPageProps {
    onLoginClick: () => void;
}

export const LandingPage: React.FC<LandingPageProps> = ({ onLoginClick }) => {
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState('');

    const handleBetaSignup = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) return;

        setStatus('loading');
        try {
            const { error } = await supabase
                .from('beta_requests')
                .insert([{ email, status: 'pending' }]);

            if (error) {
                if (error.code === '23505') {
                    setStatus('success');
                } else {
                    throw error;
                }
            } else {
                setStatus('success');
            }
        } catch (err: any) {
            console.error('Beta signup error:', err);
            setStatus('error');
            setErrorMessage(err.message || "Une erreur est survenue.");
        }
    };

    const features = [
        {
            icon: <MessageSquare className="w-8 h-8" />,
            title: "Assistant IA Conversationnel",
            description: "Décrivez votre scène en langage naturel. L'IA comprend vos intentions et génère des prompts optimisés pour la génération vidéo.",
            image: "/creative-hub.png",
            color: "from-indigo-500 to-purple-500"
        },
        {
            icon: <Layers className="w-8 h-8" />,
            title: "Mode Séquence Intelligente",
            description: "Planifiez des séquences multi-plans avec extensions automatiques. Visualisez et ajustez chaque segment avant génération.",
            image: "/creative-hub-sequence.png",
            color: "from-purple-500 to-pink-500"
        },
        {
            icon: <Play className="w-8 h-8" />,
            title: "Génération Vidéo Cinématique",
            description: "Générez des vidéos haute qualité avec VEO. Résultats cinématographiques professionnels à partir de simples descriptions.",
            image: "/resultat-prompt.png",
            color: "from-pink-500 to-red-500"
        },
        {
            icon: <Film className="w-8 h-8" />,
            title: "Timeline NLE Professionnelle",
            description: "Éditez comme sur Premiere Pro. Timeline multi-pistes, cut, trim, ripple delete, et prévisualisation temps réel.",
            image: "/timeline-nle.png",
            color: "from-red-500 to-orange-500"
        },
        {
            icon: <Users className="w-8 h-8" />,
            title: "Bibliothèque de Personnages",
            description: "Créez des personnages cohérents avec génération multi-vues (face, profil, dos). Identité vocale intégrée.",
            image: "/character-library.png",
            color: "from-orange-500 to-yellow-500"
        },
        {
            icon: <Palette className="w-8 h-8" />,
            title: "Dogmas & Styles (LoRA)",
            description: "Créez une identité visuelle unique à partir d'une seule image. Les 'Dogmas' garantissent la cohérence artistique sur toutes vos séquences.",
            image: "/dogma-full.png",
            color: "from-yellow-500 to-green-500"
        }
    ];

    return (
        <div className="min-h-screen bg-black text-white font-sans selection:bg-indigo-500/30">
            {/* Navigation */}
            <nav className="fixed top-0 w-full z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
                <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                    <div className="text-2xl font-bold bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                        Jenial
                    </div>
                    <button
                        onClick={onLoginClick}
                        className="px-5 py-2.5 text-sm font-medium bg-white/10 hover:bg-white/20 rounded-full transition-all border border-white/20"
                    >
                        Se connecter
                    </button>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative pt-32 pb-24 px-6 overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-indigo-900/30 via-black to-black" />
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMSIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />

                <div className="max-w-5xl mx-auto text-center relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 border border-indigo-500/30 text-indigo-300 text-sm font-medium mb-8">
                            <Sparkles className="w-4 h-4" />
                            Powered by Google VEO & Imagen
                        </div>

                        <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 leading-tight">
                            Le Studio de Création<br />
                            <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                                Vidéo IA Professionnel
                            </span>
                        </h1>
                        <p className="text-xl md:text-2xl text-white/60 mb-4 max-w-3xl mx-auto leading-relaxed">
                            Transformez vos idées en séquences cinématographiques.
                            De la conception à l'export, tout dans une seule interface.
                        </p>
                        <p className="text-lg text-indigo-300 mb-10 font-medium">
                            ✨ Générez des séquences continues illimitées — plus de limite de 8 secondes.
                        </p>

                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <button
                                onClick={() => document.getElementById('beta-form')?.scrollIntoView({ behavior: 'smooth' })}
                                className="px-8 py-4 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full font-bold hover:opacity-90 transition-all transform hover:scale-105 flex items-center gap-2 shadow-lg shadow-indigo-500/25"
                            >
                                Accéder à la Bêta Privée <ArrowRight className="w-5 h-5" />
                            </button>
                            <button
                                onClick={onLoginClick}
                                className="px-8 py-4 bg-white/5 border border-white/20 rounded-full font-medium hover:bg-white/10 transition-all"
                            >
                                J'ai déjà un compte
                            </button>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Features Grid */}
            <section className="py-24 px-6">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl md:text-5xl font-bold mb-4">
                            Tout ce dont vous avez besoin
                        </h2>
                        <p className="text-xl text-white/50 max-w-2xl mx-auto">
                            Un workflow complet pour les créateurs exigeants
                        </p>
                    </motion.div>

                    <div className="space-y-32">
                        {features.map((feature, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-100px" }}
                                transition={{ duration: 0.6 }}
                                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center`}
                            >
                                {/* Image */}
                                <div className="flex-1 relative group">
                                    <div className={`absolute -inset-4 bg-gradient-to-r ${feature.color} rounded-2xl opacity-20 blur-xl group-hover:opacity-30 transition-opacity`} />
                                    <div className="relative rounded-xl overflow-hidden border border-white/10 shadow-2xl">
                                        <img
                                            src={feature.image}
                                            alt={feature.title}
                                            className="w-full h-auto"
                                            loading="lazy"
                                        />
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="flex-1 lg:max-w-md">
                                    <div className={`inline-flex p-3 rounded-xl bg-gradient-to-r ${feature.color} mb-6`}>
                                        {feature.icon}
                                    </div>
                                    <h3 className="text-3xl font-bold mb-4">{feature.title}</h3>
                                    <p className="text-lg text-white/60 leading-relaxed">
                                        {feature.description}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-20 px-6 border-y border-white/10 bg-white/[0.02]">
                <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                    {[
                        { value: "VEO 3.1", label: "Dernière génération" },
                        { value: "∞", label: "Séquences continues" },
                        { value: "4K", label: "Résolution max" },
                        { value: "Pro", label: "Qualité cinéma" }
                    ].map((stat, i) => (
                        <div key={i}>
                            <div className="text-4xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent mb-2">
                                {stat.value}
                            </div>
                            <div className="text-white/50 text-sm">{stat.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Multi-Provider Openness Section */}
            <section className="py-24 px-6">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/30 text-green-300 text-sm font-medium mb-6">
                            <Zap className="w-4 h-4" />
                            Architecture Ouverte
                        </div>

                        <h2 className="text-3xl md:text-4xl font-bold mb-6">
                            Un Studio, Tous les Modèles
                        </h2>

                        <p className="text-lg text-white/60 mb-8 max-w-2xl mx-auto leading-relaxed">
                            Jenial n'est pas lié à un seul fournisseur. Notre architecture modulaire
                            est conçue pour intégrer les meilleurs modèles de génération vidéo —
                            propriétaires <strong className="text-white/80">et open source</strong>.
                        </p>

                        <div className="grid md:grid-cols-3 gap-6 mt-12">
                            <div className="p-6 rounded-xl bg-white/5 border border-white/10">
                                <div className="text-2xl mb-3">🎯</div>
                                <h3 className="font-bold text-lg mb-2">Aujourd'hui</h3>
                                <p className="text-white/50 text-sm">
                                    Google VEO 3.1, Imagen 3, Nano Banana Pro
                                </p>
                            </div>
                            <div className="p-6 rounded-xl bg-white/5 border border-white/10">
                                <div className="text-2xl mb-3">🚀</div>
                                <h3 className="font-bold text-lg mb-2">En cours</h3>
                                <p className="text-white/50 text-sm">
                                    Kling, Runway, Pika, Minimax
                                </p>
                            </div>
                            <div className="p-6 rounded-xl bg-green-500/10 border border-green-500/30">
                                <div className="text-2xl mb-3">🌐</div>
                                <h3 className="font-bold text-lg mb-2 text-green-300">Open Source</h3>
                                <p className="text-white/50 text-sm">
                                    CogVideoX, Mochi, Hunyuan — votre infrastructure
                                </p>
                            </div>
                        </div>

                        <p className="text-sm text-white/40 mt-8">
                            Gardez le contrôle. Choisissez vos modèles. Pas de vendor lock-in.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Beta Signup Section */}
            <section id="beta-form" className="py-32 px-6 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-indigo-900/10 to-transparent" />

                <div className="max-w-xl mx-auto relative z-10 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-4xl md:text-5xl font-bold mb-6">
                            Rejoignez les pionniers
                        </h2>
                        <p className="text-lg text-white/60 mb-10">
                            Accès anticipé limité. Soyez parmi les premiers à créer
                            des vidéos IA de qualité cinématographique.
                        </p>

                        <form onSubmit={handleBetaSignup} className="space-y-4">
                            <input
                                type="email"
                                placeholder="votre@email.com"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full px-6 py-4 bg-white/5 border border-white/20 rounded-xl focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all text-white placeholder:text-white/30 text-lg"
                            />
                            <button
                                type="submit"
                                disabled={status === 'loading' || status === 'success'}
                                className="w-full px-6 py-4 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl font-bold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-indigo-500/25"
                            >
                                {status === 'loading' ? (
                                    <span className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                ) : status === 'success' ? (
                                    <>Vous êtes sur la liste ! <Check className="w-5 h-5" /></>
                                ) : (
                                    <>Demander l'accès <ArrowRight className="w-5 h-5" /></>
                                )}
                            </button>
                        </form>

                        {status === 'success' && (
                            <motion.p
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="mt-6 text-green-400 font-medium"
                            >
                                ✨ Merci ! Nous vous contacterons très bientôt.
                            </motion.p>
                        )}
                        {status === 'error' && (
                            <p className="mt-4 text-red-400 text-sm">
                                {errorMessage}
                            </p>
                        )}
                    </motion.div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 text-center border-t border-white/10">
                <div className="text-2xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent mb-4">
                    Jenial
                </div>
                <p className="text-white/30 text-sm">
                    © {new Date().getFullYear()} Jenial Studio. Tous droits réservés.
                </p>
            </footer>
        </div>
    );
};
